import { MouseEvent, ReactNode } from "react";

/**
 * Opens Iubenda's cookie preferences popup (the same one as the floating
 * button), so footer links like "Cookie Policy" need no page of their own.
 *
 * HyamIubenda loads Iubenda lazily (after 5 seconds or the first user
 * interaction). A click is such an interaction, so if the API isn't there yet
 * this waits for it for a few seconds instead of doing nothing.
 */
export const openCookiePreferences = () => {
  const open = () => {
    const api = (window as any)?._iub?.cs?.api;
    if (typeof api?.openPreferences !== "function") return false;

    api.openPreferences();
    return true;
  };

  if (open()) return;

  let tries = 0;
  const interval = setInterval(() => {
    tries += 1;
    if (open() || tries >= 50) clearInterval(interval);
  }, 200);
};

const COOKIE_LINK_LABEL = /^\s*cookie[\s-]?(policy|richtlinie|einstellungen|settings)\s*$/i;

/**
 * True for a footer item that stands for the cookie preferences: either a link
 * to the (removed) cookie-policy page, or an item labelled "Cookie Policy",
 * which in English has no link in Prismic at all.
 */
export const isCookiePreferencesItem = (item: {
  link_label?: string | null;
  link?: unknown;
}) =>
  (item.link as { uid?: string } | null | undefined)?.uid === "cookie-policy" ||
  COOKIE_LINK_LABEL.test(item.link_label || "");

type HyamCookiePreferencesLinkProps = {
  className?: string;
  children: ReactNode;
};

/** A link-looking element that opens the Iubenda cookie preferences popup. */
export const HyamCookiePreferencesLink = ({
  className,
  children,
}: HyamCookiePreferencesLinkProps) => {
  const onClick = (event: MouseEvent) => {
    event.preventDefault();
    openCookiePreferences();
  };

  return (
    <a
      href="#cookie-preferences"
      role="button"
      onClick={onClick}
      className={["cursor-pointer select-none inline-block", className]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </a>
  );
};
